import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

import request from "../../../Helpers/API";
import Swal from "sweetalert2";
import { CRM_LeyendaAuditoria } from "./CRM_LeyendaAuditoria";
import { CRM_LeyendaDepartamento } from "./CRM_LeyendaDepartamento";
import { construirAuditoriaLocal } from "./construirAuditoriaLocal";
import { CRM_ChipMultiSelect } from "./CRM_ChipMultiSelect";

// Formulario vacío — usado cuando no hay cliente registrado (alta de cliente nuevo)
const FORM_VACIO = {
  lineas: [],
  giro: [],
  segunSuperficie: "",
  nivelTecnologia: "",
};

// Convierte "1,3,5" (fn_Clientes_Lineas_Obtener / fn_Clientes_Giro_Empresarial_Obtener) a array de ids numéricos
const parseIdsCSV = (csv) => (csv ? String(csv).split(",").map((v) => v.trim()).filter(Boolean).map(Number) : []);

// Convierte info_clasificacion_cliente (mdl_Info_Cliente_Clasificación_CRM) al formulario de esta pantalla
// nivelTecnologia llega en el propio info_clasificacion_cliente (clasificacion.nivel_tecnologia)
const mapClasificacionToForm = (clasificacion) => ({
  lineas: parseIdsCSV(clasificacion?.lineas),
  giro: parseIdsCSV(clasificacion?.giros),
  segunSuperficie: clasificacion?.id_superficie ?? "",
  nivelTecnologia: clasificacion?.nivel_tecnologia != null ? String(clasificacion.nivel_tecnologia) : "",
});

export const CRM_DetalleClasificacion = ({
  esNuevo,
  idcliente,
  clasificacionCliente,
  opcionesClasificacion = { lineas: [], giros: [], superficie: [] },
  opcionesGenerales = { tecnologia: [] },
  soloLectura = false,
  responsableDepartamento = []
}) => {
  const { empleado } = useSelector((store) => store.usuario);
  const valorInicial = esNuevo ? FORM_VACIO : mapClasificacionToForm(clasificacionCliente);
  const [editing, setEditing] = useState(esNuevo);
  const [form, setForm] = useState(valorInicial);
  const [auditoria, setAuditoria] = useState(clasificacionCliente);

  // clasificacionCliente llega después del primer render (fetch async en CRM_DetalleScreen) — se sincroniza aquí
  useEffect(() => {
    if (esNuevo || !clasificacionCliente) return;
    setForm(mapClasificacionToForm(clasificacionCliente));
    setAuditoria(clasificacionCliente);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clasificacionCliente]);

  // Catálogos en formato uniforme {id, display} para ChipMultiSelect y el select de superficie
  const lineasOpciones = opcionesClasificacion.lineas.map((l) => ({ id: l.idlinea, display: l.linea }));
  const girosOpciones = opcionesClasificacion.giros.map((g) => ({ id: g.idGiro_Empresarial, display: g.giro }));

  const handleEditar = () => setEditing(true);
  const handleCancelar = () => {
    setForm(valorInicial);
    setEditing(false);
  };
  const handleGuardar = () => {
    if (!form.segunSuperficie) {
      Swal.fire("CLIENTES", "Favor de seleccionar la superficie del cliente", "warning");
      return;
    }

    const body = {
      idcliente: Number(idcliente),
      lineas: form.lineas.join(","),
      giros: form.giro.join(","),
      superficie: Number(form.segunSuperficie),
      nivel_tecnologia: form.nivelTecnologia || null,
    };

    request
      .post("Clientes/GuardarDatosClasificacion", body)
      .then(async () => {
        await Swal.fire("CLIENTES", "Cambios guardados con éxito", "success");
        setAuditoria(construirAuditoriaLocal(empleado));
        setEditing(false);
      })
      .catch((error) => Swal.fire("CLIENTES", JSON.stringify(error), "error"));
  };

  const handleChange = (campo) => (e) => {
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  const handleAddValor = (campo) => (id) => {
    setForm((prev) => ({ ...prev, [campo]: [...prev[campo], id] }));
  };

  const handleRemoveValor = (campo) => (id) => {
    setForm((prev) => ({ ...prev, [campo]: prev[campo].filter((v) => v !== id) }));
  };

  return (
    <div id="sec-clasificacion" className="crm-detalle-card" style={{ scrollMarginTop: 170 }}>
      <div className="crm-detalle-card-header crm-detalle-card-header-actions">
        <div>
          <h3>Clasificación</h3>
          <p>Segmentación comercial y perfil del cliente</p>
        </div>
        <div className="crm-detalle-card-header-buttons">
          {editing ? (
            <>
              <button type="button" className="btn-icono-ghost" title="Cancelar" onClick={handleCancelar}>
                <CloseIcon />
              </button>
              <button type="button" className="btn-icono-solido" title="Guardar cambios" onClick={handleGuardar}>
                <CheckIcon />
                <span>Guardar</span>
              </button>
            </>
          ) : (
            !soloLectura && (
            <button type="button" className="btn-icono-ghost btn-editar" title="Editar" onClick={handleEditar}>
              <EditIcon />
              <span>Editar</span>
            </button>
            )
          )}
        </div>
      </div>

      <div className="crm-detalle-card-body">
        <div className="crm-detalle-grid crm-detalle-grid-1">
          <div className="crm-field">
            <label>Según superficie</label>
            <select className={editing ? "crm-field-editable" : "crm-field-readonly"} disabled={!editing} value={form.segunSuperficie} onChange={handleChange("segunSuperficie")}>
              <option value="">Seleccionar</option>
              {opcionesClasificacion.superficie.map((o) => (
                <option key={o.id} value={o.id}>{o.valor}</option>
              ))}
            </select>
          </div>
        </div>

        <CRM_ChipMultiSelect
          label="Líneas"
          help="Líneas de negocio que consume"
          valores={form.lineas}
          opciones={lineasOpciones}
          editing={editing}
          onAdd={handleAddValor("lineas")}
          onRemove={handleRemoveValor("lineas")}
        />

        <CRM_ChipMultiSelect
          label="Giro"
          help="Giro del cliente"
          valores={form.giro}
          opciones={girosOpciones}
          editing={editing}
          onAdd={handleAddValor("giro")}
          onRemove={handleRemoveValor("giro")}
        />

        <div className="crm-detalle-grid crm-detalle-grid-1">
          <div className="crm-field">
            <label>Nivel de tecnología</label>
            <select className={editing ? "crm-field-editable" : "crm-field-readonly"} disabled={!editing} value={form.nivelTecnologia} onChange={handleChange("nivelTecnologia")}>
              <option value="">Seleccionar</option>
              {opcionesGenerales.tecnologia.map((o) => (
                <option key={o.id} value={String(o.id)}>{o.valor}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="crm-detalle-card-footer">
        <CRM_LeyendaDepartamento responsables={responsableDepartamento} idrel={6} />
        <CRM_LeyendaAuditoria auditoria={auditoria} />
      </div>
    </div>
  );
};
